import type {
  AnalyticsQuery,
  MetricName,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import {
  RightBarBody,
  RightBarLayout,
} from "@v1/components/layouts/RightBarLayout";
import { calculateDelta } from "@v1/features/analytics/analytics";
import { OVERVIEW_TABLE_METRICS } from "@v1/features/analytics/constants";
import MetricWithDelta from "@v1/features/analytics/MetricWithDelta";
import { useAnalyticsOnboardingOAuthLink } from "@v1/features/analytics/useAnalyticsOnboardingOAuthLink";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import usePublishedInfo from "@v1/hooks/usePublishedInfo";
import { setIsRightBarAnalyticsOpen } from "@v1/reducers/ui-reducer";
import { useEditorDispatch } from "@v1/store/store";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import { DEFAULT_QUERY } from "@v2/features/analytics/constants";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useUrlHosts from "@v2/features/analytics/hooks/useUrlHosts";
import { generateDeepDiveNavigationPathFromUrl } from "@v2/features/analytics/utils/deepDiveNavigation";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useOpenModal } from "@v2/store/modalStore";
import { getRangesFromTimePeriod } from "analytics-utils/analytics";
import { ArrowRight, Lock, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { convertDaysToMs } from "replo-utils/lib/datetime";

type AnalyticsMetricCardProps = {
  title: string;
  doesCompareValueExist: boolean;
  mainValue: number | undefined;
  compareValue: number | undefined;
  metricKey: MetricName;
  chartValues: number[] | undefined;
  isLoading: boolean;
};

const BoxWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <div className="border-0.5 border-border shadow-xs flex flex-col items-start justify-start rounded-md bg-white">
      <div className="flex grow flex-col p-4">{children}</div>
    </div>
  );
};

const UpgradeBox = () => {
  const openModal = useOpenModal();
  const openBillingModal = () => {
    return openModal({
      type: "billingErrorModal",
      props: {
        billingPlanMessageKey: "billingPlan.analytics",
      },
    });
  };
  return (
    <>
      <div className="mb-2 flex">
        <div className="bg-surface text-neutral flex flex-row items-center gap-1 rounded-sm p-1">
          <Lock size={12} />
          <span className="text-xs">Paid Feature</span>
        </div>
      </div>
      <div className="text-default mb-2 text-sm font-semibold">
        Unlock Replo Analytics
      </div>
      <div className="text-muted mb-4 text-xs">
        See how your pages are performing in real time with Replo Analytics
      </div>
      <DeprecatedButton
        size="sm"
        variant="primary"
        isLoading={false}
        onClick={openBillingModal}
      >
        Unlock Analytics
      </DeprecatedButton>
    </>
  );
};

const ConnectShopifyBox = () => {
  const productAnalytics = useLogAnalytics();
  const { oauthLink, isLoading } = useAnalyticsOnboardingOAuthLink();
  return (
    <>
      <img
        src="/replo-shopify-connector.svg"
        alt="Connect Replo To Shopify"
        className="mb-4 block object-scale-down"
      />
      <div className="text-default mb-2 text-sm font-semibold">
        Connect to see store Analytics
      </div>
      <div className="text-muted mb-4 text-xs">
        Get started by enabling the Replo Pixel through Shopify
      </div>
      <DeprecatedButton
        size="sm"
        variant="primary"
        to={oauthLink ?? ""}
        isLoading={isLoading || !oauthLink}
        onClick={() => {
          return productAnalytics("analytics.connect", {
            tab: "analytics_tab",
          });
        }}
      >
        Connect Shopify
      </DeprecatedButton>
    </>
  );
};

const AnalyticsMetricCard: React.FC<AnalyticsMetricCardProps> = ({
  title,
  doesCompareValueExist,
  mainValue,
  compareValue,
  metricKey,
  chartValues,
  isLoading,
}) => {
  if (isLoading) {
    return <Skeleton className="h-[80px] w-full" />;
  }

  return (
    <div className="border-0.5 border-border grid grid-cols-12 rounded-lg px-6 py-4">
      <div className="col-span-7 flex flex-col gap-2">
        <span className="text-muted text-xs">{title}</span>
        <MetricWithDelta
          name={metricKey}
          value={mainValue ?? 0}
          delta={calculateDelta(mainValue, compareValue)}
          doesCompareValueExist={doesCompareValueExist}
          wrapperClassName="gap-2"
          valueClassName="text-sm font-semibold"
        />
      </div>
      <div className="col-span-4 col-end-13">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={100}
            height={250}
            data={chartValues?.map((value) => ({ value }))}
          >
            <Line
              type="monotone"
              dataKey="value"
              stroke="#2463EB"
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DummyLoadedAnalyticsMenuPane: React.FC = () => {
  return (
    <div className="flex h-full flex-col gap-3">
      {OVERVIEW_TABLE_METRICS.map(({ label: metricTitle, key: metricKey }) => {
        const mainValue = Math.floor(Math.random() * 800);
        const compareValue = Math.floor(Math.random() * 800);

        return (
          <AnalyticsMetricCard
            key={metricKey}
            metricKey={metricKey}
            title={metricTitle}
            doesCompareValueExist={true}
            mainValue={mainValue}
            compareValue={compareValue}
            chartValues={Array.from({ length: 7 }, () => {
              return Math.floor(Math.random() * 800);
            })}
            isLoading={false}
          />
        );
      })}
    </div>
  );
};

type LoadedAnalyticsMenuPaneProps = {
  query: AnalyticsQuery;
};
const LoadedAnalyticsMenuPane: React.FC<LoadedAnalyticsMenuPaneProps> = ({
  query,
}) => {
  const { ranges } = query;
  const chartRanges = {
    ...ranges,
    mainRange: {
      ...ranges.mainRange,
      interval: convertDaysToMs(1),
    },
    compareAtRanges: [],
  };

  const chartQuery = { ...query, ranges: chartRanges };

  const {
    mainRangeResults,
    compareAtRangeResults,
    isLoading: isRangeResultsLoading,
  } = useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: query,
  });

  const { mainRangeResults: chartMainRangeResults, isLoading: isChartLoading } =
    useBasicAnalyticsRead({
      type: "withoutUrlSyncQuery",
      queryConfig: chartQuery,
    });

  const isLoading = isRangeResultsLoading || isChartLoading;

  const compareAtRangeViewsArray = compareAtRangeResults?.[0]?.metrics.views;

  const doesCompareValueExist = Boolean(compareAtRangeViewsArray);

  return (
    <div className="flex flex-col gap-3">
      {OVERVIEW_TABLE_METRICS.map(({ label: metricTitle, key: metricKey }) => {
        const mainValue = mainRangeResults[0]?.metrics[metricKey]?.[0];
        const compareValue = compareAtRangeResults[0]?.metrics[metricKey]?.[0];

        const chartValues = chartMainRangeResults[0]?.metrics[metricKey];

        return (
          <AnalyticsMetricCard
            key={metricKey}
            metricKey={metricKey}
            title={metricTitle}
            doesCompareValueExist={doesCompareValueExist}
            mainValue={mainValue}
            compareValue={compareValue}
            chartValues={chartValues}
            isLoading={isLoading}
          />
        );
      })}
    </div>
  );
};

const SetupAnalyticsMenuPane: React.FC<{ isCurrentlyPaying: boolean }> = ({
  isCurrentlyPaying,
}) => {
  return (
    <div className="relative h-full">
      <DummyLoadedAnalyticsMenuPane />
      <div className="backdrop-blur-xs absolute inset-0 flex h-full w-full flex-col items-center justify-center bg-white/10">
        <BoxWrapper>
          {isCurrentlyPaying ? <ConnectShopifyBox /> : <UpgradeBox />}
        </BoxWrapper>
      </div>
    </div>
  );
};

type AnalyticsMenuPaneProps = {
  width: number;
  elementRef: React.RefObject<HTMLDivElement>;
};
export const AnalyticsMenuPane: React.FC<AnalyticsMenuPaneProps> = ({
  width,
  elementRef,
}) => {
  const { workspaceId, workspace } = useCurrentWorkspaceContext();
  const { project } = useCurrentProjectContext();
  const projectId = project?.id;
  const { shopifyUrl, customDomainUrl, path } = usePublishedInfo();

  const {
    isEnabled,
    isCurrentlyPaying,
    isLoading: isLoadingAnalyticsStatus,
  } = useAnalyticsStatus();

  const dispatch = useEditorDispatch();

  const logEvent = useLogAnalytics();

  const url = customDomainUrl ?? shopifyUrl ?? null;

  const showSetupPage =
    !Boolean(url) || (!isEnabled && !isLoadingAnalyticsStatus);

  const urlHostsObj = useUrlHosts();

  if ((!workspaceId && !projectId) || !path) {
    return null;
  }

  const { urlSyncedQuery, deepDivePathWithCompressedUrlSyncedQuery } =
    generateDeepDiveNavigationPathFromUrl({
      url,
      urlHosts: urlHostsObj.data ?? [],
      path,
      workspaceId: workspaceId ?? "",
      projectId: projectId ?? null,
      selectedTimePeriod: {
        type: "relative",
        value: "last-7-days",
      },
    });

  const { updatedRanges: ranges } = getRangesFromTimePeriod({
    selectedTimePeriod: urlSyncedQuery.selectedTimePeriod,
    timeZone: project?.timeZone ?? workspace?.timeZone ?? "UTC",
  });

  /**
   * NOTE (Max, 2024-10-17): We use a basic query where we get:
   * 1) urlHosts from the useUrlHosts hook
   * 2) mainRange datetimes from the Experiment's createdAt / endedAt dates
   * 3) filters using the experimentId: we're only interested in CH rows that contain
   * the experimentId as a urlParam
   */
  const query: AnalyticsQuery = {
    ...DEFAULT_QUERY,
    urlHosts: urlSyncedQuery.urlHosts,
    ranges,
    filters: urlSyncedQuery.filters,
    mode: "groupByUrlPath",
    isFractional: false,
  };

  return (
    <RightBarLayout rightBarWidth={width} rightBarElementRef={elementRef}>
      <div className="flex flex-row justify-between gap-1 px-3 py-4">
        <div
          className={twMerge(
            "flex flex-col gap-2",
            showSetupPage && "opacity-30",
          )}
        >
          <span className="text-sm font-semibold">Page Analytics</span>
          <span className="text-muted text-xs">Showing Last 7 days</span>
        </div>
        <div className="flex flex-col justify-between">
          <div className="flex flex-row justify-end">
            <X
              size={20}
              onClick={() => {
                dispatch(setIsRightBarAnalyticsOpen(false));
              }}
              className="cursor-pointer"
            />
          </div>
          <div
            className={twMerge(
              "text-primary flex flex-row items-center gap-1",
              showSetupPage && "pointer-events-none opacity-30",
            )}
          >
            <Link
              to={deepDivePathWithCompressedUrlSyncedQuery ?? ""}
              className="text-xs"
              onClick={() => {
                logEvent("analytics.editor.menu.deepDive.click", {});
              }}
            >
              See more
            </Link>
            <ArrowRight size={12} />
          </div>
        </div>
      </div>
      <RightBarBody isEditMode={false} className="px-2">
        {showSetupPage ? (
          <SetupAnalyticsMenuPane isCurrentlyPaying={isCurrentlyPaying} />
        ) : (
          <LoadedAnalyticsMenuPane query={query} />
        )}
      </RightBarBody>
    </RightBarLayout>
  );
};
